// Deep links resolve their entity's sport (#416).
//
// Every /public/* call is stamped with the active sport, and the entity
// lookups (games/:id, players/:id, …) 404 for an entity of another sport.
// A shared or reloaded link to a non-default-sport game, player or team
// therefore showed "not found", and the off-sport auto-switch could never
// fire. This gate runs on the session's initial URL only (where the active
// sport is just the tenant default or the fan's stored pick), on multi-sport
// tenants only, for entity pages only:
//   1. one unscoped lookup of the entity (cached per URL);
//   2. if its sport differs from the active sport, switch to it, and in any
//      case store it as the fan's pick, so the chrome's restore keeps it;
//   3. then mount the page, whose own requests now carry the right sport.
// While the lookup runs the gate suspends: for classic that is the
// app-level PageLoader the lazy detail chunk shows anyway; templates with a
// Frame show their RouteLoading inside it. In-app navigation is not gated
// (lists are sport-filtered, so their links are already in the active sport).
//
// #568: an entity of a disabled/archived sport is not public. The gate never
// switches to (or stores) a sport outside the tenant's public sports: it
// mounts the page as is, whose own request (stamped with the active sport)
// gives the page's usual not-found.

import { useLayoutEffect, type ReactNode } from 'react'
import { useParams } from 'react-router-dom'
import { useLeague } from '@/contexts/LeagueContext'
import { useSportContext } from '@/contexts/SportContext'
import { readStoredSport, tenantSportIds } from '@/contexts/storedSport'
import type { SitePageKey } from './pages'
import { ENTITY_LOOKUPS, isInitialPath, readEntitySport, type Params } from './entitySport'

export function EntitySportGate({ page, pathname, children }: { page: SitePageKey; pathname: string; children: ReactNode }) {
  const params = useParams() as Params
  const { league } = useLeague()
  const { activeSportId, setActiveSport } = useSportContext()
  const lookup = ENTITY_LOOKUPS[page]
  const endpoint = lookup ? lookup(params) : null
  const initial = isInitialPath(pathname)
  const sports = tenantSportIds(league)
  const gated = Boolean(endpoint) && initial && sports.length >= 2
  const found = gated ? readEntitySport(endpoint!) : null // suspends while the lookup runs
  const sport = found && sports.includes(found) ? found : null
  const mismatch = Boolean(sport && sport !== activeSportId)

  useLayoutEffect(() => {
    if (!sport) return
    // Switch (if needed) and store the entity's sport as the fan's pick, so the
    // restore the chrome runs on mount keeps it. Layout effect: the switch is
    // committed before paint, so the page never mounts with the wrong sport.
    if (sport !== activeSportId || readStoredSport() !== sport) setActiveSport(sport, { persist: true })
  }, [sport, activeSportId, setActiveSport])

  if (mismatch) return null
  return <>{children}</>
}
